/**
 * Telegram account mode (sources.md "telegram"): connect this device's Telegram account (phone → code →
 * two-step password) through `engine.sources.telegram`, then pick a chat; the chosen chat becomes the
 * source (`config: { mode: 'account', peerId }`). The session stays in this device's SecretStore.
 */

import { useEffect, useState } from 'react';
import type { TelegramAuthState, TelegramDialog } from '@digester/core';
import { useEngine } from '../../engine-client/EngineProvider.js';
import { Icon } from '../../icons/Icon.js';
import { Field } from '../../ui/form.js';
import { describeSourceError } from './errors.js';

export interface TelegramAccountProps {
  peerId: string;
  onPick(dialog: TelegramDialog): void;
}

const DIALOGS = 200;

export function TelegramAccount({ peerId, onPick }: TelegramAccountProps) {
  const engine = useEngine();
  const [auth, setAuth] = useState<TelegramAuthState | null>(null);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogs, setDialogs] = useState<TelegramDialog[] | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let live = true;
    engine.sources.telegram.authState().then(
      (s) => live && setAuth(s),
      (e: unknown) => live && setError(describeSourceError(e)),
    );
    return () => {
      live = false;
    };
  }, [engine]);

  useEffect(() => {
    if (auth?.state !== 'signed-in' || dialogs) return;
    engine.sources.telegram.dialogs(DIALOGS).then(setDialogs, (e: unknown) => setError(describeSourceError(e)));
  }, [auth, dialogs, engine]);

  const step = async (run: () => Promise<TelegramAuthState>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      setAuth(await run());
      setInput('');
    } catch (e) {
      setError(describeSourceError(e));
    } finally {
      setBusy(false);
    }
  };

  const errorBox = error ? (
    <div className="test-error" role="alert">
      <Icon name="circle-alert" />
      <span>{error}</span>
    </div>
  ) : null;

  if (!auth) return errorBox ?? <p className="field-help">Checking the Telegram connection on this device…</p>;

  if (auth.state !== 'signed-in') {
    const label = auth.state === 'signed-out' ? 'Phone number' : auth.state === 'code-sent' ? 'Code from Telegram' : 'Two-step verification password';
    const button = auth.state === 'signed-out' ? 'Send code' : 'Sign in';
    return (
      <div className="telegram-connect">
        <p className="field-help">Connect your Telegram account on this device to read your channels, groups and chats. The session stays on this device.</p>
        <Field
          label={label}
          name="telegram-input"
          type={auth.state === 'password-needed' ? 'password' : auth.state === 'signed-out' ? 'tel' : 'text'}
          value={input}
          autoComplete="off"
          placeholder={auth.state === 'signed-out' ? '+381 60 1234567' : auth.state === 'password-needed' ? (auth.hint ?? '') : '12345'}
          onChange={setInput}
        />
        <button
          type="button"
          className="secondary-button"
          disabled={busy || input.trim() === ''}
          onClick={() =>
            void step(() =>
              auth.state === 'signed-out'
                ? engine.sources.telegram.sendCode(input.trim())
                : auth.state === 'code-sent'
                  ? engine.sources.telegram.submitCode(input.trim())
                  : engine.sources.telegram.submitPassword(input),
            )
          }
        >
          <Icon name="send" /> {button}
        </button>
        {errorBox}
      </div>
    );
  }

  const q = search.trim().toLowerCase();
  const shown = (dialogs ?? []).filter((d) => d.title.toLowerCase().includes(q) || (d.username ?? '').toLowerCase().includes(q));
  return (
    <div className="telegram-connect">
      <div className="read-only-row">
        <span>Connected as</span>
        <strong>{auth.displayName}</strong>
      </div>
      <label className="vb-picker-search telegram-search">
        <Icon name="search" />
        <input type="search" aria-label="Search chats" placeholder="Search chats…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </label>
      <div className="telegram-dialogs" role="radiogroup" aria-label="Pick a chat">
        {dialogs === null ? <p className="field-help">Loading chats…</p> : null}
        {shown.map((d) => (
          <label key={d.peerId} className={d.peerId === peerId ? 'selected' : undefined}>
            <input type="radio" name="telegram-peer" value={d.peerId} checked={d.peerId === peerId} onChange={() => onPick(d)} />
            <Icon name={d.peerType === 'channel' ? 'radio' : d.peerType === 'group' ? 'users' : 'user'} />
            <span>{d.title}</span>
            <small>{d.username ? `@${d.username}` : d.peerType}</small>
          </label>
        ))}
      </div>
      <button type="button" className="text-link" onClick={() => void step(async () => (await engine.sources.telegram.signOut(), { state: 'signed-out' }))}>
        Disconnect Telegram on this device
      </button>
      {errorBox}
    </div>
  );
}
